import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, ArrowRight, Clock, Shield } from 'lucide-react';

export default function CTABanner() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-16 md:py-24 bg-[#111A2E] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-[#F95738]/10 via-transparent to-[#38BDF8]/10" />
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#F95738]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#38BDF8]/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            data-testid="fast-quote-cta-left"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your{' '}
              <span className="text-[#F95738]">Roof</span>?
            </h2>
            
            <p className="text-lg text-[#94A3B8] mb-8">
              Get a free, no-obligation quote within 24 hours. Our team is ready to bring your 
              roof back to life with premium materials and expert craftsmanship.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#38BDF8]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#38BDF8]" />
                </div>
                <div>
                  <div className="text-white font-medium">Quick Response</div>
                  <div className="text-sm text-[#94A3B8]">24-hour quotes</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F95738]/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#F95738]" />
                </div>
                <div>
                  <div className="text-white font-medium">15 Year Warranty</div>
                  <div className="text-sm text-[#94A3B8]">Peace of mind</div>
                </div>
              </div>
            </div>

            {/* Phone CTA */}
            <motion.a
              href="tel:0400000000"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-4 glass px-6 py-4 rounded-2xl group"
            >
              <div className="w-14 h-14 rounded-full bg-[#38BDF8] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#94A3B8]">Call us now</div>
                <div className="text-2xl font-bold text-white">0400 000 000</div>
              </div>
            </motion.a>
          </motion.div>

          {/* Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
            data-testid="fast-quote-cta-right"
          >
            <div className="glass rounded-2xl p-8 md:p-10 text-center glow-border">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F95738]/20 text-[#F95738] text-sm font-medium mb-6">
                <Clock className="w-4 h-4" />
                Limited Time Offer
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Get Your Fast Quote Today
              </h3>
              
              <p className="text-[#94A3B8] mb-8">
                Free inspection, honest advice, and competitive pricing for all roof types
              </p>

              <motion.a
                href="#quote"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 cta-button px-10 py-5 rounded-full text-white font-bold text-lg w-full justify-center"
              >
                Get Fast Quote
                <ArrowRight className="w-5 h-5" />
              </motion.a>

              <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#94A3B8]">
                <span>✓ No obligation</span>
                <span>✓ Free inspection</span>
              </div>
            </div>

            {/* Floating elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-4 -right-4 glass px-4 py-2 rounded-full"
            >
              <span className="text-[#FBBF24] font-bold">5.0 ★</span>
            </motion.div>
            
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
              className="absolute -bottom-4 -left-4 glass px-4 py-2 rounded-full"
            >
              <span className="text-[#38BDF8] font-bold">114 Reviews</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
